import { motion } from "framer-motion";
import { Apple, ChevronDown } from "lucide-react";
import KnotFigure from "@/components/KnotFigure";

const LINES = [
  { text: "Everything about", serif: false },
  { text: "your boat.", serif: false },
  { text: "In one place.", serif: true },
];

export default function Hero() {
  return (
    <section className="relative overflow-hidden bg-[#0A192F] pt-[72px]" data-testid="hero-section">
      <div
        className="pointer-events-none absolute inset-0"
        style={{
          background:
            "radial-gradient(900px 500px at 75% 20%, rgba(29,138,153,0.16), transparent 60%), radial-gradient(700px 500px at 10% 90%, rgba(29,138,153,0.08), transparent 60%)",
        }}
      />
      <div
        className="pointer-events-none absolute inset-0 opacity-[0.05]"
        style={{
          backgroundImage:
            "linear-gradient(rgba(255,255,255,0.5) 1px, transparent 1px), linear-gradient(90deg, rgba(255,255,255,0.5) 1px, transparent 1px)",
          backgroundSize: "72px 72px",
        }}
      />

      <div className="relative mx-auto grid max-w-7xl gap-16 px-5 pb-28 pt-16 sm:px-8 lg:grid-cols-[1.15fr_0.85fr] lg:items-center lg:gap-12 lg:pt-24">
        <div>
          <motion.p
            initial={{ opacity: 0, y: 14 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.1 }}
            className="font-mono-label text-[11px] uppercase tracking-[0.3em] text-[#25A5B7]"
            data-testid="hero-overline"
          >
            Knot Forgotten · for iPhone
          </motion.p>

          <h1
            className="mt-6 text-4xl font-extrabold leading-[1.06] tracking-tight text-white sm:text-5xl lg:text-6xl"
            data-testid="hero-headline"
          >
            {LINES.map((line, i) => (
              <span key={line.text} className="mask-line">
                <motion.span
                  className={`block ${line.serif ? "font-serif-display font-semibold italic text-[#25A5B7]" : ""}`}
                  initial={{ y: "115%" }}
                  animate={{ y: 0 }}
                  transition={{ duration: 0.9, delay: 0.25 + i * 0.14, ease: [0.22, 1, 0.36, 1] }}
                >
                  {line.text}
                </motion.span>
              </span>
            ))}
          </h1>

          <motion.p
            initial={{ opacity: 0, y: 18 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.7, delay: 0.75 }}
            className="mt-7 max-w-xl text-base leading-relaxed text-slate-300 sm:text-lg"
            data-testid="hero-subcopy"
          >
            Keep your boat's important information, maintenance history, service records,
            reminders, and other boat details organized in one simple app.
          </motion.p>

          <motion.div
            initial={{ opacity: 0, y: 18 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.7, delay: 0.9 }}
            className="mt-10 flex flex-wrap items-center gap-5"
          >
            <button
              type="button"
              disabled
              data-testid="hero-cta-app-store-disabled"
              title="The App Store link will be activated at launch"
              className="inline-flex cursor-not-allowed items-center gap-3 rounded-full border border-white/20 bg-white/[0.06] px-7 py-4 text-sm font-semibold text-slate-200"
            >
              <Apple className="h-5 w-5" />
              Coming Soon to the App Store
            </button>
            <span className="font-mono-label text-[11px] uppercase tracking-[0.2em] text-slate-500">
              Includes a free 30-day Plus trial
            </span>
          </motion.div>
        </div>

        <KnotFigure />
      </div>

      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ delay: 1.6, duration: 0.8 }}
        className="relative mx-auto flex max-w-7xl justify-center pb-10"
      >
        <motion.span
          animate={{ y: [0, 8, 0] }}
          transition={{ repeat: Infinity, duration: 2.2, ease: "easeInOut" }}
          className="text-slate-500"
        >
          <ChevronDown className="h-5 w-5" />
        </motion.span>
      </motion.div>
    </section>
  );
}
